import React, { useState } from "react";
import { Badge, Form, Spinner } from "react-bootstrap";
import { FaChevronDown, FaChevronRight, FaEllipsisV, FaPen, FaCheck, FaTimes } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import UserInitialsAvatar from "../../../../utils/userInitialAvatar";
import UserAllocationModal from "../users/UserAllocationModal";
import { getFullName } from "../../../../utils/helpers";
import { fetchTimeDistributionData, updateWorkPackageMember } from "../../../../redux/slices/timeDistributionSlice";

const MemberRow = ({ member, resources, wpId, month }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  
  const [showModal, setShowModal] = useState(false);
  const resourceData = resources.find((r) => r.id === member.user_id);
  const hasConflict = resourceData?.has_conflict;

  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(member.etis || 0);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const parsedValue = parseFloat(value);
      
      await dispatch(updateWorkPackageMember({ 
        wpId, 
        userId: member.user_id, 
        etis: parsedValue,
        month,
      })).unwrap();

      dispatch(fetchTimeDistributionData({ month }));
      setIsEditing(false);
    } catch (err) {
      console.error(t('components.planningTab.errors.save_wp_eti'), err);
      setValue(member.etis || 0);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setValue(member.etis || 0);
    setIsEditing(false);
  };

  return (
    <tr className={hasConflict ? "table-danger bg-opacity-10" : ""}>
      <td className="py-2" style={{ paddingLeft: "5.5rem" }}>
        <div className="d-flex align-items-center gap-2">
          <UserInitialsAvatar
            first_name={member.first_name || ""}
            last_name={member.last_name || ""}
            style={{ width: "22px", height: "22px", fontSize: "0.65rem", flexShrink: 0 }}
          />
          <span className={`fw-semibold ${hasConflict ? "txt-danger" : "txt-main"}`} style={{ fontSize: "0.85rem" }}>
            {getFullName(member)}
          </span>
        </div>
      </td>

      <td className="text-center">
        {isEditing ? (
          <div className="d-flex align-items-center justify-content-center gap-1">
            <Form.Control
              type="number"
              step="0.05"
              min="0"
              max="1"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              style={{ width: "70px", fontSize: "0.8rem" }}
              size="sm"
              disabled={isSaving}
              autoFocus
            />
            {isSaving ? (
              <Spinner animation="border" size="sm" className="mx-1" style={{ width: "12px", height: "12px", color: 'var(--brand-primary)' }} />
            ) : (
              <>
                <FaCheck role="button" className="text-success" onClick={handleSave} />
                <FaTimes role="button" className="text-danger" onClick={handleCancel} />
              </>
            )}
          </div>
        ) : (
          <Badge
            bg="white"
            text="dark"
            className="border px-2 py-1 d-inline-flex align-items-center gap-2 shadow-sm"
            role="button"
            onClick={() => setIsEditing(true)}
          >
            {Number(value).toFixed(2)}
            <FaPen size={10} className="txt-subtle" />
          </Badge>
        )}
      </td>

      <td className={`text-end px-2 ${hasConflict ? "txt-danger fw-semibold" : "txt-subtle"}`} style={{ fontSize: "0.8rem" }}>
        {resourceData
          ? (hasConflict
              ? t('components.planningTab.table.conflict_total', { eti: resourceData.total_eti?.toFixed(2) })
              : t('components.planningTab.table.alloc_total', { eti: resourceData.total_eti?.toFixed(2) }))
          : ""}
      </td>
      <td className="text-center px-3" style={{ width: "40px" }}>
        <FaEllipsisV 
          role="button" 
          className="txt-subtle" 
          onClick={() => setShowModal(true)} 
          title={t('components.planningTab.table.manage_allocations')} 
        />
      </td>

      <UserAllocationModal
        show={showModal}
        onHide={() => setShowModal(false)}
        userId={member.user_id}
        userName={getFullName(member)}
        month={month}
      />
    </tr>
  );
};

const WorkPackageRow = ({ workPackage, resources, isExpanded, onToggle, month }) => {
  const { t } = useTranslation(); 

  return (
    <>
      <tr onClick={onToggle} style={{ cursor: "pointer", backgroundColor: "rgba(0,0,0,0.02)" }}>
        <td className="ps-5 py-2">
          <span className="me-2 txt-subtle" style={{ fontSize: "0.7rem" }}>
            {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
          </span>
          <span className="fw-semibold txt-main">{workPackage.name}</span>
          <span className="txt-subtle ms-2" style={{ fontSize: "0.8rem" }}>
            ({workPackage.members.length === 1 
              ? t('components.planningTab.table.member_count', { count: workPackage.members.length }) 
              : t('components.planningTab.table.member_count_plural', { count: workPackage.members.length })
            })
          </span>
        </td>
        <td className="text-center" style={{ fontSize: "0.85rem" }}>
          <Badge bg="light" text="dark" className="border px-2 py-1 shadow-sm opacity-75">
            {workPackage.etis.toFixed(2)} {t('components.planningTab.table.package_eti')}
          </Badge>
        </td>
        <td></td>
        <td></td>
      </tr>

      {isExpanded && (
        workPackage.members.length === 0 ? (
          <tr>
            <td colSpan={4} className="py-2 txt-subtle" style={{ paddingLeft: "5.5rem", fontSize: "0.8rem" }}>
              {t('components.planningTab.empty_states.no_members')}
            </td>
          </tr>
        ) : (
          [...workPackage.members]
            .sort((a, b) =>
              getFullName(a).toLowerCase().localeCompare(getFullName(b).toLowerCase(), 'pt')
            )
            .map((member) => (
              <MemberRow
                key={`member-${member.id || member.user_id}`}
                member={member}
                resources={resources}
                wpId={workPackage.id} 
                month={month}
              />
            ))
        )
      )}
    </>
  );
};

export default WorkPackageRow;
